import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { api, apiError } from "@/api";
import { compressImage } from "@/utils/imageCompress";
import logoUrl from "@/assets/ankhara-logo-mark.png";
import { Camera, ArrowRight } from "lucide-react";

const WORKERS = 4;
const CHUNK_THRESHOLD = 7.5 * 1024 * 1024;
const isVideoName = (name) => /\.(mp4|mov|webm)$/i.test(name);

const track = (name, props = {}) => {
  try { window.posthog?.capture(name, props); } catch { /* noop */ }
  try { window.gtag?.("event", name, props); } catch { /* noop */ }
};

export default function Upload() {
  const { token } = useParams();
  const [info, setInfo] = useState(undefined); // undefined = loading, null = invalid link
  const [handle, setHandle] = useState(() => localStorage.getItem("ank_handle") || "");
  const [queue, setQueue] = useState([]); // {file, name, status: optimizing|pending|uploading|done|failed|duplicate, pct, error}
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // {count, status}
  const [consent, setConsent] = useState(false);
  const inputRef = useRef(null);

  const uploadChunked = async (it) => {
    const { data: start } = await api.post(`/upload/${token}/start`, { filename: it.file.name, size: it.file.size });
    const cs = start.chunk_size;
    const total = Math.ceil(it.file.size / cs);
    for (let i = 0; i < total; i += 1) {
      const blob = it.file.slice(i * cs, (i + 1) * cs);
      await api.post(`/upload/${token}/chunk/${start.upload_id}/${i}`, blob, {
        headers: { "Content-Type": "application/octet-stream" },
      });
      const pct = Math.round(((i + 1) / total) * 95);
      setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, pct } : x)));
    }
    const { data } = await api.post(`/upload/${token}/finish`, {
      upload_id: start.upload_id,
      filename: it.file.name,
      total_chunks: total,
      handle: handle.trim() || undefined,
      consent: true,
    });
    return { chunked: true, data };
  };

  useEffect(() => {
    api.get(`/upload/${token}`)
      .then(({ data }) => { setInfo(data); track("community_upload_open", { kind: data.kind }); })
      .catch(() => setInfo(null));
  }, [token]);

  const startUpload = async (targets) => {
    if (!targets.length) return;
    setBusy(true);
    let cursor = 0;
    let savedCount = 0;
    let failCount = 0;
    const worker = async () => {
      while (cursor < targets.length) {
        const it = targets[cursor];
        cursor += 1;
        setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "uploading", pct: 0 } : x)));
        try {
          let saved = false;
          let duplicate = false;
          let failReason = null;
          if (isVideoName(it.name) || it.file.size > CHUNK_THRESHOLD) {
            await uploadChunked(it);
            saved = true;
          } else {
            const fd = new FormData();
            if (handle.trim()) fd.append("handle", handle.trim());
            fd.append("files", it.file);
            const { data } = await api.post(`/upload/${token}`, fd, {
              onUploadProgress: (ev) => {
                const pct = ev.total ? Math.round((ev.loaded / ev.total) * 100) : 0;
                setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, pct } : x)));
              },
            });
            if ((data.skipped || []).includes(it.name)) duplicate = true;
            else if ((data.failed || []).length) failReason = data.failed[0]?.reason || "Rejected by server";
            else saved = true;
          }
          if (duplicate) {
            setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "duplicate" } : x)));
          } else if (failReason) {
            failCount += 1;
            setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "failed", error: failReason } : x)));
          } else if (saved) {
            savedCount += 1;
            setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "done", pct: 100 } : x)));
          }
        } catch (err) {
          const msg = err?.response?.status === 413 ? "File too large for one request" : apiError(err);
          failCount += 1;
          setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "failed", error: msg } : x)));
        }
      }
    };
    await Promise.all(Array.from({ length: WORKERS }, worker));
    setBusy(false);
    if (handle.trim()) localStorage.setItem("ank_handle", handle.trim());
    if (savedCount > 0 && failCount === 0) {
      track("community_upload_complete", { kind: info.kind, count: savedCount });
      setDone({ count: savedCount, status: info.kind === "photographer" ? "approved" : "pending" });
    } else if (savedCount > 0) {
      track("community_upload_partial", { kind: info.kind, count: savedCount, failed: failCount });
    }
  };

  const onPick = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length || !info || busy) return;
    const items = picked.map((f) => ({
      file: f, name: f.name,
      status: info.kind === "community" ? "optimizing" : "pending",
      pct: 0, error: "",
    }));
    setQueue(items);
    setDone(null);
    if (info.kind === "community") {
      for (const it of items) {
        if (isVideoName(it.name)) {
          setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, status: "pending" } : x)));
          continue;
        }
        const small = await compressImage(it.file);
        it.file = small;
        setQueue((q) => q.map((x) => (x.name === it.name ? { ...x, file: small, status: "pending" } : x)));
      }
    }
    if (consent) {
      startUpload(items); // auto-upload once consent is ticked
    }
  };

  // ticking consent with a waiting queue starts the upload
  useEffect(() => {
    if (!consent || busy) return;
    const waiting = queue.filter((q) => q.status === "pending");
    if (waiting.length > 0) startUpload(waiting);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent]);

  const retryFailed = () => startUpload(queue.filter((q) => q.status === "failed"));

  const failedCount = queue.filter((q) => q.status === "failed").length;

  const pendingCount = queue.filter((q) => q.status === "pending" || q.status === "failed").length;

  if (info === undefined) {
    return <div className="upl-page"><p className="com-loader" data-testid="upload-loading">FINDING THE ROLL…</p></div>;
  }

  if (info === null) {
    return (
      <div className="upl-page" data-testid="upload-invalid">
        <header className="upl-head">
          <a href="/" data-testid="upload-home-link"><img src={logoUrl} alt="ANKHARA" className="com-logo" /></a>
        </header>
        <main className="upl-main upl-center">
          <h1 className="upl-title">THIS LINK IS DEAD</h1>
          <p className="upl-sub">Upload links get rotated between sessions. Grab the current one at the door or from our stories.</p>
          <a href="/community" className="upl-go upl-link" data-testid="upload-invalid-feed-link">SEE THE FEED <ArrowRight size={16} /></a>
        </main>
      </div>
    );
  }

  if (done) {
    return (
      <div className="upl-page" data-testid="upload-success">
        <header className="upl-head">
          <a href="/" data-testid="upload-home-link"><img src={logoUrl} alt="ANKHARA" className="com-logo" /></a>
        </header>
        <main className="upl-main upl-center">
          <h1 className="upl-title" data-testid="upload-success-title">
            {done.status === "approved" ? "ON THE FEED" : "IN THE DARKROOM"}
          </h1>
          <p className="upl-sub" data-testid="upload-success-sub">
            {done.status === "approved"
              ? `${done.count} frame${done.count === 1 ? "" : "s"} live on THE FEED right now.`
              : `${done.count} frame${done.count === 1 ? "" : "s"} with the organizers — approved shots hit THE FEED.`}
          </p>
          <a href="/community" className="upl-go upl-link" data-testid="upload-feed-link">VIEW THE FEED <ArrowRight size={16} /></a>
          <button type="button" className="upl-more" onClick={() => { setQueue([]); setDone(null); }} data-testid="upload-more-btn">
            ADD MORE FRAMES
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="upl-page" data-testid="upload-page">
      <header className="upl-head">
        <a href="/" data-testid="upload-home-link"><img src={logoUrl} alt="ANKHARA" className="com-logo" /></a>
        <span className="upl-head-tag" data-testid="upload-kind">
          {info.kind === "photographer" ? "PHOTOGRAPHER DROP" : "COMMUNITY DROP"} — SESSION {info.session}
        </span>
      </header>
      <main className="upl-main">
        <h1 className="upl-title">{info.kind === "photographer" ? "SEND THE ROLL" : "PUT YOUR FRAME ON THE WALL"}</h1>
        <p className="upl-sub">
          {info.kind === "photographer"
            ? "Full-resolution originals. Straight to the feed, no queue."
            : "Every frame is developed by the organizers before it hits the feed. Add your handle — it gets printed on the polaroid."}
        </p>

        <div
          className="upl-drop"
          data-testid="upload-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files); }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
        >
          <Camera size={44} strokeWidth={1.4} />
          <span className="upl-drop-main">TAP TO PICK PHOTOS</span>
          <span className="upl-drop-sub">
            {info.kind === "community"
              ? "photos + short videos — camera files welcome (HEIC, CR2, CR3, DNG, TIFF), we convert them for you"
              : "jpg / png / webp / heic / raw / mp4 / mov — we convert camera files for you"}
          </span>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,video/mp4,video/quicktime,video/webm,.heic,.heif,.cr2,.cr3,.dng,.tif,.tiff,.mp4,.mov,.webm"
            multiple
            hidden
            data-testid="upload-file-input"
            onChange={(e) => { onPick(e.target.files); e.target.value = ""; }}
          />
        </div>

        <input
          type="text"
          className="upl-handle"
          placeholder="@yourhandle (optional)"
          value={handle}
          maxLength={30}
          onChange={(e) => setHandle(e.target.value)}
          data-testid="upload-handle-input"
          aria-label="Your social media handle"
        />

        {queue.length > 0 && (
          <div className="upl-q" data-testid="upload-queue">
            {queue.map((q) => (
              <div key={q.name} className="upl-q-row" data-testid={`upload-row-${q.name}`}>
                <span className="upl-q-name">{q.name}</span>
                <span className="upl-q-bar"><span className="upl-q-fill" style={{ width: `${q.pct}%` }} /></span>
                <span className={`upl-q-st upl-q-${q.status}`} data-testid={`upload-status-${q.name}`}>
                  {q.status === "optimizing" && "SHRINKING"}
                  {q.status === "pending" && "QUEUED"}
                  {q.status === "uploading" && `${q.pct}%`}
                  {q.status === "done" && "SENT"}
                  {q.status === "duplicate" && "DUP"}
                  {q.status === "failed" && (q.error || "FAILED")}
                </span>
              </div>
            ))}
          </div>
        )}

        <label className="upl-consent" data-testid="upload-consent-wrap">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
            data-testid="upload-consent" />
          <span>I took this and I'm OK with ANKHARA featuring it</span>
        </label>

        {busy && (
          <p className="upl-working" data-testid="upload-working">UPLOADING — KEEP THIS TAB OPEN…</p>
        )}
        {!busy && queue.some((q) => q.status === "pending") && !consent && (
          <p className="upl-consent-hint" data-testid="upload-consent-hint">TICK THE CONSENT LINE TO START THE UPLOAD</p>
        )}
        {!busy && failedCount > 0 && (
          <button
            type="button"
            className="upl-go"
            onClick={retryFailed}
            data-testid="upload-submit-btn"
          >
            UPLOAD FRAMES — RETRY {failedCount} FAILED
          </button>
        )}
      </main>
    </div>
  );
}
